import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AzureDevOpsIcon } from "@/components/icons";
import { AzureDevOpsPatDialog } from "@/components/settings/azure-devops-pat-dialog";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Key,
  Layers,
} from "lucide-react";

export function AzureDevOpsIntegration() {
  const [isPatDialogOpen, setIsPatDialogOpen] = useState(false);
  const [initialOrganization, setInitialOrganization] = useState("");
  const [initialDisplayName, setInitialDisplayName] = useState("");

  const {
    data: connections,
    isLoading,
    refetch,
  } = trpc.integration.listConnections.useQuery({ provider: "azure_devops" });

  const adoConnections =
    connections?.filter((c) => c.provider === "azure_devops") ?? [];
  const hasConnections = adoConnections.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <AzureDevOpsIcon className="h-4 w-4 text-sky-500 fill-current" />
                Azure DevOps Boards
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect Azure DevOps Boards with delegated{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">
                  vso.work_write
                </code>{" "}
                or Personal Access Token (PAT) to track work items, sync states,
                and post PR gate status.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={() => {
                  setInitialOrganization("");
                  setInitialDisplayName("");
                  setIsPatDialogOpen(true);
                }}
                variant="default"
                size="sm"
                className="gap-1.5"
              >
                <Key className="h-3.5 w-3.5" />
                Connect PAT
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
            </div>
          ) : !hasConnections ? (
            <div className="flex items-center gap-2.5 rounded-lg bg-muted/30 p-4 text-sm text-muted-foreground">
              <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>
                No Azure DevOps organizations connected yet. Use the Connect PAT
                button above to link an organization.
              </span>
            </div>
          ) : (
            <div className="divide-y divide-border/60 rounded-lg bg-muted/20">
              {adoConnections.map((conn) => {
                const isNeedsReauth = conn.status === "needs_reauth";
                const isActive = conn.status === "active";
                const isExpired = Boolean(conn.isExpired);
                const isExpiringSoon = Boolean(conn.isExpiringSoon);
                const expiresAt = conn.patExpiresAt
                  ? new Date(conn.patExpiresAt)
                  : null;

                return (
                  <div
                    key={conn.id}
                    className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium">
                          {conn.displayName || conn.externalSiteId}
                        </p>
                        {isActive && !isExpired && (
                          <Badge
                            variant="outline"
                            className="rounded-sm gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-xs"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </Badge>
                        )}
                        {isExpired && (
                          <Badge
                            variant="destructive"
                            className="rounded-sm gap-1 text-xs"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            PAT Expired
                          </Badge>
                        )}
                        {!isExpired && isExpiringSoon && (
                          <Badge
                            variant="outline"
                            className="rounded-sm gap-1 border-amber-500/30 bg-amber-500/10 text-amber-600 text-xs"
                          >
                            <Clock className="h-3 w-3" />
                            PAT Expiring Soon
                          </Badge>
                        )}
                        {isNeedsReauth && (
                          <Badge
                            variant="destructive"
                            className="rounded-sm gap-1 text-xs"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Re-auth Required
                          </Badge>
                        )}
                        {!isActive && !isNeedsReauth && !isExpired && (
                          <Badge
                            variant="secondary"
                            className="rounded-sm capitalize text-xs"
                          >
                            {conn.status}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {conn.siteUrl && (
                          <a
                            href={conn.siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:underline hover:text-foreground"
                          >
                            <span>{conn.siteUrl}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {expiresAt && (
                          <span
                            className={
                              isExpired
                                ? "text-destructive font-medium"
                                : isExpiringSoon
                                  ? "text-amber-600 font-medium"
                                  : ""
                            }
                          >
                            Expires: {expiresAt.toLocaleDateString()}
                          </span>
                        )}
                        {conn.webhookRouteId && (
                          <span className="font-mono text-xs text-muted-foreground">
                            Route: {conn.webhookRouteId}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setInitialOrganization(
                            conn.siteUrl || conn.externalSiteId,
                          );
                          setInitialDisplayName(conn.displayName || "");
                          setIsPatDialogOpen(true);
                        }}
                      >
                        Update PAT
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AzureDevOpsPatDialog
        open={isPatDialogOpen}
        onOpenChange={setIsPatDialogOpen}
        initialOrganization={initialOrganization}
        initialDisplayName={initialDisplayName}
        onConnected={refetch}
      />
    </>
  );
}
